import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const AUTO_MS = 4500;

export const FlyerShowcase = () => {
  const [flyers, setFlyers] = useState([]);
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/flyers")
      .then((res) => setFlyers(res.data || []))
      .catch(() => setFlyers([]))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (flyers.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % flyers.length), AUTO_MS);
    return () => clearInterval(t);
  }, [flyers.length]);

  if (!loaded || flyers.length === 0) return null;

  const go = (dir) => setIndex((i) => (i + dir + flyers.length) % flyers.length);
  const current = flyers[index];
  const prev = flyers[(index - 1 + flyers.length) % flyers.length];
  const next = flyers[(index + 1) % flyers.length];
  const showSides = flyers.length > 2;

  const sideCard = (flyer, side) => (
    <button
      key={`${side}-${flyer.id}`}
      onClick={() => go(side === "left" ? -1 : 1)}
      data-testid={`flyer-peek-${side}`}
      aria-label={side === "left" ? "Previous flyer" : "Next flyer"}
      className="hidden md:block relative w-[220px] aspect-square rounded-2xl overflow-hidden border border-cream/10 opacity-50 scale-90 hover:opacity-80 hover:scale-[0.93] transition-all duration-500 cursor-pointer shrink-0"
    >
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}${flyer.image_url}`}
        alt={flyer.caption || "Flyer"}
        className="w-full h-full object-cover"
      />
    </button>
  );

  return (
    <section className="pt-16 md:pt-20 px-4 sm:px-8 lg:px-16" data-testid="flyer-showcase">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-8"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-3">Upcoming Tours & Offers</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream">
            Fresh flyers, <span className="italic text-gold">fresh journeys.</span>
          </h2>
        </motion.div>

        <div className="flex items-center justify-center gap-6">
          {showSides && sideCard(prev, "left")}

          <div className="relative w-full max-w-[400px]">
            <div className="relative aspect-square rounded-3xl overflow-hidden border border-gold/25 bg-card shadow-2xl shadow-black/40">
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={current.id}
                  src={`${process.env.REACT_APP_BACKEND_URL}${current.image_url}`}
                  alt={current.caption || "Tour flyer"}
                  data-testid={`flyer-slide-${index}`}
                  initial={{ opacity: 0, x: 60, rotateY: -8 }}
                  animate={{ opacity: 1, x: 0, rotateY: 0 }}
                  exit={{ opacity: 0, x: -60, rotateY: 8 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              {current.caption && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/85 to-transparent pt-12 pb-4 px-5">
                  <p className="text-cream text-xs sm:text-sm font-medium text-center" data-testid="flyer-caption">
                    {current.caption}
                  </p>
                </div>
              )}
              {flyers.length > 1 && (
                <>
                  <button
                    onClick={() => go(-1)}
                    data-testid="flyer-prev-btn"
                    aria-label="Previous flyer"
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-ink/70 border border-gold/30 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => go(1)}
                    data-testid="flyer-next-btn"
                    aria-label="Next flyer"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-ink/70 border border-gold/30 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              )}
            </div>

            {flyers.length > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4" data-testid="flyer-dots">
                {flyers.map((f, i) => (
                  <button
                    key={f.id}
                    onClick={() => setIndex(i)}
                    data-testid={`flyer-dot-${i}`}
                    aria-label={`Go to flyer ${i + 1}`}
                    className={`relative h-1.5 rounded-full overflow-hidden transition-all duration-300 ${
                      i === index ? "w-10 bg-cream/15" : "w-2.5 bg-cream/20 hover:bg-gold/50"
                    }`}
                  >
                    {i === index && (
                      <motion.span
                        key={`progress-${index}`}
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: AUTO_MS / 1000, ease: "linear" }}
                        className="absolute inset-y-0 left-0 bg-gold rounded-full"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {showSides && sideCard(next, "right")}
        </div>
      </div>
    </section>
  );
};
